import React, { useState, useMemo } from 'react'
import AppBar from '../components/AppBar'
import SearchBar from '../components/SearchBar'
import UserListItem from '../components/UserListItem'
import SmallBadgeButton from '../components/SmallBadgeButton'
import { userApi } from '../api/user'
import { FollowerStatus } from '../types/follow'
import LoadingSpinner from '../components/LoadingSpinner'
import Gap from '../components/Gap'
import { pageCache } from '../utils/pageCache'

type FollowRequestItem = {
  id: string
  nickname: string
  username: string
  profile_image: string | null
  status: FollowerStatus
}

/** 스와이프로 돌아왔을 때 이전 목록을 그대로 보여주기 위한 키 */
const CACHE_KEY = 'follow-requests:me'

const FollowRequests: React.FC = () => {
  const [requests, setRequests] = useState<FollowRequestItem[]>(
    () => pageCache.get<FollowRequestItem[]>(CACHE_KEY) ?? []
  )
  const [searchValue, setSearchValue] = useState('')
  const [loading, setLoading] = useState(false)

  React.useEffect(() => {
    const loadRequests = async () => {
      try {
        // 이미 그려져 있으면 스피너로 덮지 않는다
        if (!pageCache.has(CACHE_KEY)) {
          setLoading(true)
        }
        const response = await userApi.getFollowRequests()
        const requestItems: FollowRequestItem[] = response.requests.map(
          (req) => ({
            ...req,
            status: req.status as FollowerStatus,
          })
        )
        pageCache.set(CACHE_KEY, requestItems)
        setRequests(requestItems)
      } catch (error) {
        console.error('팔로우 요청 목록 조회 실패:', error)
      } finally {
        setLoading(false)
      }
    }

    loadRequests()
  }, [])

  const filteredRequests = useMemo(() => {
    if (!searchValue.trim()) {
      return requests
    }

    const searchLower = searchValue.toLowerCase()
    return requests.filter(
      (request) =>
        request.nickname.toLowerCase().startsWith(searchLower) ||
        request.username.toLowerCase().startsWith(searchLower)
    )
  }, [requests, searchValue])

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
  }

  const handleAccept = async (id: string) => {
    try {
      await userApi.acceptFollow(id)
      // 데이터 다시 로드
      const response = await userApi.getFollowRequests()
      const requestItems: FollowRequestItem[] = response.requests.map(
        (req) => ({
          ...req,
          status: req.status as FollowerStatus,
        })
      )
      setRequests(requestItems)
    } catch (error) {
      console.error('팔로우 수락 실패:', error)
    }
  }

  const handleReject = async (id: string) => {
    try {
      await userApi.rejectFollow(id)
      setRequests((prevRequests) =>
        prevRequests.filter((request) => request.id !== id)
      )
    } catch (error) {
      console.error('팔로우 거절 실패:', error)
    }
  }

  const renderSearchBar = () => {
    return (
      <div className="w-full px-20px py-16px">
        <SearchBar
          placeholder="검색"
          value={searchValue}
          onSearch={handleSearchChange}
        />
      </div>
    )
  }

  const renderLoading = () => {
    return (
      <div className="w-full flex-1 flex flex-col items-center justify-center empty-state-center">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden relative">
        {/* 헤더 */}
        <AppBar title={`${requests.length} 팔로우 요청`} isBottomBorder />

        {/* 검색 바 */}
        {renderSearchBar()}

        {/* 목록 */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-16px pb-16px flex flex-col relative">
          {loading ? (
            <>{renderLoading()}</>
          ) : filteredRequests.length > 0 ? (
            <div className="w-full flex flex-col gap-16px">
              {filteredRequests.map((request) => (
                <UserListItem
                  key={request.id}
                  id={request.id}
                  nickname={request.nickname}
                  username={request.username}
                  actionButtons={
                    <div className="flex items-center">
                      <SmallBadgeButton
                        label="승인"
                        variant="default"
                        onClick={() => handleAccept(request.id)}
                      />
                      <Gap width={4} />
                      <SmallBadgeButton
                        label="거절"
                        variant="inverse"
                        onClick={() => handleReject(request.id)}
                      />
                    </div>
                  }
                />
              ))}
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center px-16px pointer-events-none empty-state-center">
              <p className="text-style-body1 text-textColorText3 text-center">
                {searchValue.trim()
                  ? '검색 결과가 없습니다'
                  : '아직 팔로우 요청이 없습니다'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FollowRequests
